import {
  assign,
  chain,
  cloneDeep,
  find,
  findIndex,
  get,
  isEqual,
  merge,
} from 'lodash';

import { isNumber } from '@/utils/misc';
import CustomError from '@/utils/custom-error';

export const cssUnitsRegex = /^([+-]?[0-9]*\.?(?:[0-9]+)?)(px|em|rem|ex|%|in|cm|mm|pt|pc)?$/;
export const toPixel = (value) => (isNumber(value) ? `${value}px` : value);
export const toPercentage = (value) => (isNumber(value) ? `${100 * value}%` : value);
export const isDef = (n) => n !== undefined && n !== null && !Number.isNaN(n);
export const isObject = (obj) => {
  const type = typeof obj;
  return obj !== null && (type === 'object' || type === 'function');
};

export function objectCount(collection, desc) {
  return chain(collection)
    .countBy((i) => get(i, desc))
    .map((value, key) => [key, value])
    .sortBy((pair) => pair[1])
    .value();
}

export function mapToNameCount(array) {
  return array.map((item) => ({
    name: item[0],
    count: item[1],
  }));
}

export function extractNameByMayorCount(collection, desc) {
  if (Array.isArray(collection)) {
    const result = objectCount(collection, desc);
    return result.pop()[0];
  }
  return collection;
}

export function getSchemaIndexFromStatus(schema, statuses = []) {
  // Set default schema for no option or when the fund have multiple statuses
  let schemaIndex = 0;
  schema.forEach((item, index) => {
    if (isEqual(item.status, statuses)) {
      schemaIndex = index;
    }
  });
  return schemaIndex;
}

export function applySchemaToOptions(schemaOptions, defaultOptions) {
  const options = [];
  let defaultOption = {};
  let item = {};
  schemaOptions.forEach((option) => {
    // If the option is a String will take all the default option configuration
    if (typeof option === 'string') {
      defaultOption = find(defaultOptions, { label: option });
      item = merge({}, defaultOption);
    }
    // If the option is an Object will override the default option configuration
    if (typeof option === 'object') {
      defaultOption = find(defaultOptions, { label: option.label });
      item = merge({}, defaultOption, option);
    }
    options.push(item);
  });
  return options;
}

export function overrideSchema(schemaOptions, newSchemaOptions) {
  const schema = schemaOptions;

  newSchemaOptions.forEach((item) => {
    const index = getSchemaIndexFromStatus(schemaOptions, item.status);
    schema[index].options = item.options;
  });

  return schema;
}

export function processColumns(target, source) {
  const _target = cloneDeep(target);
  source.columns.forEach((column) => {
    if (column.__action === 'merge') {
      const columnIndex = findIndex(_target.columns, (c) => c.id === column.id);
      _target.columns[columnIndex] = assign(_target.columns[columnIndex], column);
    }
    if (column.__action === 'calc') {
      const columnIndex = findIndex(_target.columns, (c) => c.id === column.id);
      if (column.__operation === 'add') {
        _target.columns[columnIndex][column.__targetColumn] += column.__value;
      }
    }
    if (column.__action === 'add') {
      _target.columns.splice(column.__pos, 0, column);
    }
  });
  return _target;
}

export function isJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function formatJsonResponse(data) {
  let jsonData = data;
  if (data && isJsonString(data)) {
    jsonData = JSON.parse(data);
  }
  return jsonData;
}

export function getMessageFromResponse(response, withError = true) {
  let message = get(response, 'message') || get(response, 'status.message') || '';
  if (withError) {
    message = get(response, 'error.response.status.message') || message;
  }
  return message;
}

export function reviewErrorFromResponse(response, throwError = true) {
  if (response instanceof Response && !response.ok) {
    throw new CustomError(response, `(${get(response, 'status')}) ${get(response, 'statusText', '')}`);
  }
  if (throwError && response && (response.error || get(response, 'status.statusCode') >= 400)) {
    throw new CustomError(response, `${getMessageFromResponse(response)}. ${get(response, 'error.message', '')}`);
  }
  return true;
}

export function goToLogoutRoute($route, $router) {
  if ($route.name !== 'logout') {
    $router.push('/logout');
  }
}

export function goToRoute(currentRoute, $router, route) {
  if (currentRoute.name !== route && currentRoute.path !== route) {
    $router.push(route);
  }
}

export function scrollToTop() {
  window.scrollTo(0, 0);
}
